import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EventIcon from '@mui/icons-material/Event';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { announcementService, getStrapiMediaUrl } from '../services/api';

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const response = await announcementService.getAll({
          populate: {
            attachments: true,
            events: {
              populate: '*'
            }
          },
          sort: ['createdAt:desc']
        });
        
        if (response && response.data) {
          // Transform Strapi response to match our component's expected format
          const formattedAnnouncements = response.data.map(item => {
            return {
              id: item.id,
              title: item.title || item.Title || 'Untitled Announcement',
              date: new Date(item.createdAt || item.publishedAt || new Date().toISOString()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }),
              content: item.content || item.Content || '',
              category: item.category || item.Category || '',
              isPinned: item.isPinned || false,
              isNew: new Date(item.createdAt || item.publishedAt || new Date()) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
              
              attachments: (item.attachments || [])
                .map(attachment => {
                try {
                  return {
                    id: attachment.id,
                    name: attachment.name || 'Attachment',
                    url: getStrapiMediaUrl(attachment.url) || '#',
                    type: attachment.mime?.split('/')[1] || 'file'
                  };
                } catch (err) {
                  console.error('Error parsing attachment:', err);
                  return null;
                }
              }).filter(Boolean),
              
              events: (item.events || [])
                .map(event => {
                try {
                  return {
                    id: event.id,
                    title: event.title || event.Title || '',
                    isTBA: event.isTBA || false,
                    date: event.isTBA ? 'TBA' : event.startDate ? new Date(event.startDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric'
                    }) : 'TBA',
                    location: event.location || '',
                    content: event.content || event.Content || ''
                  };
                } catch (err) {
                  console.error('Error parsing event:', err);
                  return null;
                }
              }).filter(Boolean)
            };
          });
          
          setAnnouncements(formattedAnnouncements);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching announcements:', err);
        setError('Failed to load announcements. Please try again later.');
        setLoading(false);
      }
    };

    fetchAnnouncements();
    
  }, []);

  const loadMore = () => {
    setVisibleCount(prevCount => prevCount + 5);
  };

  return (
    <>
      <Helmet>
        <title>Community Announcements | Brynleigh Estates HOA</title>
        <meta name="description" content="Stay updated with the latest news, announcements, and community updates from Brynleigh Estates Homeowners Association in Chelsea, Alabama." />
        <link rel="canonical" href="https://brynleighestates.com/announcements" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/announcements" />
        <meta property="og:title" content="Community Announcements | Brynleigh Estates HOA" />
        <meta property="og:description" content="Stay updated with the latest news, announcements, and community updates from Brynleigh Estates Homeowners Association in Chelsea, Alabama." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Community Announcements | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Stay updated with the latest news, announcements, and community updates from Brynleigh Estates Homeowners Association." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        title="Community Announcements" 
        subtitle="Stay informed with the latest news and updates from our community"
        imageUrl="/images/hills-bg.jpeg"
      />
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Latest Updates
          </Typography>
          <Divider sx={{ mb: 4 }} />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>
          ) : announcements.length === 0 ? (
            <Alert severity="info" sx={{ my: 4 }}>No announcements available at this time.</Alert>
          ) : (
            <>
              <Grid container spacing={3}>
                {announcements.slice(0, visibleCount).map((announcement, index) => (
                  <Grid item xs={12} key={announcement.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card 
                        elevation={3}
                        sx={{ 
                          borderRadius: 2,
                          overflow: 'hidden',
                          transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: 6
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h5" component="h3" gutterBottom>
                                {announcement.title}
                                {announcement.isNew && (
                                  <Chip 
                                    icon={<FiberNewIcon />} 
                                    label="New" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ ml: 1, verticalAlign: 'middle' }} 
                                  />
                                )}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="subtitle2" color="text.secondary">
                                  {announcement.date}
                                </Typography>
                              </Box>
                            </Box>
                            {announcement.category && (
                              <Chip 
                                label={announcement.category} 
                                color="secondary" 
                                size="small" 
                                sx={{ ml: 2 }} 
                              />
                            )}
                          </Box>
                          
                          <Box sx={{ 
                            '& a': { color: 'primary.main' },
                            '& ul, & ol': { pl: 2 },
                            '& p': { my: 0.5 },
                            '& img': { maxWidth: '100%', height: 'auto' }
                          }}>
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{announcement.content}</ReactMarkdown>
                          </Box>
                          
                          {announcement.events && announcement.events.length > 0 && (
                            <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                                Related Events
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {announcement.events.map(event => (
                                  <Box key={event.id} sx={{ p: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                    <Typography variant="subtitle2">{event.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Date/Time: {event.date} 
                                      {event.location && ` • Location: ${event.location}`}
                                    </Typography>
                                    {event.content && (
                                      <Box 
                                        sx={{ 
                                          mt: 1,
                                          '& a': { color: 'primary.main' },
                                          '& ul, & ol': { pl: 2 },
                                          '& p': { my: 0.5 },
                                          '& img': { maxWidth: '100%', height: 'auto' }
                                        }}
                                      >
                                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>{event.content}</ReactMarkdown>
                                      </Box>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button 
                                  variant="outlined" 
                                  size="small" 
                                  startIcon={<EventIcon />}
                                  component="a"
                                  href="/events"
                                >
                                  View Calendar
                                </Button>
                              </Box>
                            </Box>
                          )}
                          
                          {announcement.attachments && announcement.attachments.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Attachments:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {announcement.attachments.map(attachment => (
                                  <Button
                                    key={attachment.id}
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                      attachment.type === 'pdf' ? <PictureAsPdfIcon /> : 
                                      attachment.type === 'event' ? <EventIcon /> : 
                                      <AttachFileIcon />
                                    }
                                    component="a"
                                    href={attachment.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ mr: 1, mb: 1 }}
                                  >
                                    {attachment.name}
                                  </Button>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
              
              {visibleCount < announcements.length && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={loadMore}
                    size="large"
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default Announcements; 